import {
  initAgentWidget as initAgentWidgetFn,
  getAgentWidgetHandles as getAgentWidgetHandlesFn,
  type AgentWidgetInitHandle
} from "./runtime/init";

export type {
  AgentWidgetMessageAuthor,
  AgentWidgetModelMessageTransform,
  AgentWidgetModelMessageProjection,
  AgentWidgetDictationConfig,
  DictationProviderConfig,
  DictationPhase,
  DictationState,
  DictationStateEvent,
  DictationRenderContext,
  DictationRenderHandle,
  AgentWidgetConfig,
  AgentWidgetLoadingLabelConfig,
  AgentWidgetExpansionConfig,
  LoadingLabelAnimationContext,
  LoadingLabelAnimationHandle,
  AgentWidgetStorageAdapter,
  AgentWidgetStoredState,
  AgentWidgetConfigPatch,
  AgentWidgetSessionInitTrigger,
  AgentWidgetSessionInitHook,
  AgentWidgetVoicePrewarmTrigger,
  AgentWidgetVoicePrewarmHook,
  TargetResolver,
  ResolvedTarget,
  AgentWidgetFeatureFlags,
  AgentWidgetArtifactsFeature,
  AgentWidgetArtifactsLayoutConfig,
  PersonaArtifactDisplayMode,
  PersonaArtifactDisplayModeInput,
  PersonaArtifactDisplayRules,
  PersonaArtifactFilesDisplayRules,
  PersonaArtifactKind,
  PersonaArtifactPresentation,
  PersonaArtifactRecord,
  PersonaArtifactActionContext,
  PersonaArtifactStatusLabelContext,
  PersonaArtifactCustomAction,
  PersonaArtifactManualUpsert,
  WidgetPreferenceCapabilities,
  WidgetArtifactLayoutPreference,
  WidgetPreferenceSlice,
  ArtifactConfigPayload,
  AgentWidgetInitOptions,
  AgentWidgetMessage,
  AgentWidgetMessageRole,
  AgentWidgetModelMessageRole,
  AgentWidgetTurnActivity,
  AgentWidgetTurnActivityConfig,
  AgentWidgetLauncherConfig,
  AgentWidgetLauncherTeaserConfig,
  AgentWidgetTooltipConfig,
  AgentWidgetDockConfig,
  AgentWidgetEvent,
  AgentWidgetStreamParser,
  AgentWidgetStreamParserResult,
  AgentWidgetRequestPayload,
  // Context provider types (e.g. for config.contextProviders)
  AgentWidgetContextProvider,
  AgentWidgetContextProviderContext,
  // Context mention types (e.g. for config.contextMentions)
  AgentWidgetContextMentionConfig,
  AgentWidgetContextMentionSource,
  AgentWidgetContextMentionItem,
  AgentWidgetContextMentionPayload,
  AgentWidgetContextMentionRef,
  AgentWidgetContextMentionSearchContext,
  AgentWidgetContextMentionResolveContext,
  AgentWidgetContextMentionMenuRenderContext,
  AgentWidgetContextMentionItemRenderContext,
  AgentWidgetContextMentionChipRenderContext,
  AgentWidgetContextMentionTokenRenderContext,
  // Per-mention LLM block formatter entry (config.contextMentions.llmFormat)
  AgentWidgetMentionLlmEntry,
  // Skills / slash-commands (config.contextMentions.triggers)
  AgentWidgetMentionTriggerChannel,
  AgentWidgetMentionTriggerPosition,
  AgentWidgetContextMentionCommandContext,
  AgentWidgetContextMentionComposerCapability,
  AgentWidgetCustomFetch,
  AgentWidgetSSEEventParser,
  AgentWidgetSSEEventResult,
  AgentWidgetHeadersFunction,
  // Multi-modal content types
  TextContentPart,
  ImageContentPart,
  ContentPart,
  MessageContent,
  // Attachment config type
  AgentWidgetAttachmentsConfig,
  AgentWidgetAttachmentPreviewConfig,
  AttachmentPreviewRemoveConfig,
  AttachmentPreviewState,
  AttachmentPreviewRenderContext,
  AttachmentPreviewRenderHandle,
  AgentWidgetComposerConfig,
  // Composer state + submission snapshot (controller.getComposerState,
  // persona:composer:state, composer.onBeforeSend)
  ComposerState,
  ComposerPhase,
  ComposerAttachmentState,
  ComposerQuote,
  ComposerSubmissionSnapshot,
  ComposerSubmissionOptions,
  ComposerBeforeSendResult,
  // History + asynchronous interaction (session.resubmitFrom,
  // composer.streamingSubmitBehavior, persisted drafts)
  ComposerResubmitOptions,
  ComposerStreamingSubmitBehavior,
  AgentWidgetStoredDraft,
  // Composer action registry (composer.actions, contributeComposerActions)
  ComposerAction,
  ComposerActionBase,
  ComposerActionPlacement,
  ComposerActionPresentation,
  ComposerActionVisibility,
  ComposerButtonAction,
  ComposerCustomAction,
  ComposerActionContext,
  ComposerActionRenderContext,
  ComposerActionContributionContext,
  ComposerActionOverflowConfig,
  ComposerOverflowMenuConfig,
  ComposerMenuItem,
  ComposerMenuRenderContext,
  ComposerMenuRenderHandle,
  // Model picker (composer.models, composer.modelPicker)
  ComposerModel,
  ComposerModelPickerConfig,
  ComposerModelPickerPresentation,
  // Composer modes + mode groups (composer.modes, composer.modeGroups)
  ComposerMode,
  ComposerModeChipVisibility,
  ComposerModeGroup,
  ComposerModeGroupPresentation,
  // Composer baseline configuration (submit keys, sizing, locks)
  ComposerLayout,
  ComposerSubmitKey,
  ComposerInputAttributes,
  ComposerDisabledOption,
  // Attachment upload adapter (attachments.adapter)
  AgentWidgetAttachmentAdapter,
  AttachmentUploadContext,
  // Layout types
  AgentWidgetLayoutConfig,
  AgentWidgetHeaderLayoutConfig,
  AgentWidgetMessageLayoutConfig,
  AgentWidgetMessageRoleLayout,
  AgentWidgetAvatarConfig,
  AgentWidgetTimestampConfig,
  WidgetLayoutSlot,
  SlotRenderer,
  SlotRenderResult,
  AgentWidgetLinkClickContext,
  SlotRenderContext,
  HeaderRenderContext,
  MessageRenderContext,
  // Markdown types
  AgentWidgetMarkdownConfig,
  AgentWidgetMarkdownOptions,
  AgentWidgetMarkdownRendererOverrides,
  // Message actions types
  AgentWidgetMessageActionsConfig,
  AgentWidgetMessageFeedback,
  AgentWidgetCustomMessageAction,
  AgentWidgetCustomMessageActionRole,
  // Client token types
  ClientSession,
  ClientInitResponse,
  PreparedClientSession,
  ClientChatRequest,
  ClientFeedbackRequest,
  ClientFeedbackType,
  // Message injection types
  InjectMessageOptions,
  InjectAssistantMessageOptions,
  InjectUserMessageOptions,
  InjectSystemMessageOptions,
  InjectComponentDirectiveOptions,
  // Loading indicator types
  LoadingIndicatorRenderContext,
  AgentWidgetLoadingIndicatorConfig,
  // Idle indicator types
  IdleIndicatorRenderContext,
  // Agent execution types
  AgentConfig,
  AgentLoopConfig,
  AgentToolsConfig,
  AgentRequestOptions,
  AgentExecutionState,
  AgentMessageMetadata,
  AgentMessagePhase,
  AgentWidgetAgentRequestPayload,
  // Approval types
  AgentWidgetApproval,
  AgentWidgetApprovalConfig,
  AgentWidgetApprovalDecisionOptions,
  // WebMCP: page-discovered tool consumption
  AgentWidgetWebMcpConfig,
  ClientToolDefinition,
  ToolAnnotations,
  WebMcpConfirmHandler,
  WebMcpConfirmInfo,
  WebMcpToolResult,
  // Event stream types
  SSEEventRecord,
  EventStreamConfig,
  EventStreamBadgeColor,
  EventStreamViewRenderContext,
  EventStreamRowRenderContext,
  EventStreamToolbarRenderContext,
  EventStreamPayloadRenderContext,
  // Controller event map
  AgentWidgetControllerEventMap,
  AgentWidgetEventContext,
  AgentWidgetRunStartedEvent,
  AgentWidgetRunFinishedEvent,
  AgentWidgetReadAloudEvent,
  // Ask-user-question (built-in answer-pill sheet) types
  AskUserQuestionPayload,
  AskUserQuestionPrompt,
  AskUserQuestionOption,
  AgentWidgetAskUserQuestionFeature,
  AgentWidgetAskUserQuestionStyles,
  // Visitor conversation history (client-token mode) types
  AgentWidgetHistoryFeature,
  AgentWidgetOpenConversationOptions,
  AgentWidgetRevealToolCallOptions,
  BackgroundConversationAdapter,
  ConversationExecutionState,
  AgentWidgetHistoryListAction,
  AgentWidgetHistoryCopy,
  HistoryScope,
  HistoryIdentityStatus,
  ResolvedHistoryPresentation,
  HistoryReturnSurface,
  HistoryConversationSummary,
  HistoryConversationPatch,
  HistoryConversationPage,
  HistoryConversationDetail,
  HistoryDisplayProjection,
  // History rendering customization (plugin render hooks; types only)
  ResolvedHistoryCopy,
  HistoryViewState,
  HistoryPendingAction,
  AgentWidgetHistoryRenderActions,
  AgentWidgetRenderHistoryViewContext,
  AgentWidgetRenderHistoryHeaderContext,
  AgentWidgetRenderHistoryConversationContext,
  AgentWidgetRenderHistoryStateContext,
  AgentWidgetRenderHistoryOpenErrorContext,
  ClientVisitorGrant,
  AgentWidgetSuggestion,
  AgentWidgetSuggestionChipsConfig,
  AgentWidgetResolvedSuggestion,
  AgentWidgetSuggestionBehavior,
  AgentWidgetSuggestionVariant,
  AgentWidgetSuggestionSurface,
  AgentWidgetSuggestionSource,
  AgentWidgetSuggestionSurfaceConfig,
  AgentWidgetStarterSuggestionsConfig,
  AgentWidgetFollowUpSuggestionsConfig,
  AgentWidgetSuggestionsConfig,
  AgentWidgetWelcomeConfig,
  AgentWidgetWelcomeIcon,
  AgentWidgetWelcomeIconBase,
  AgentWidgetWelcomeIconPlacement,
  AgentWidgetWelcomeAlign,
  AgentWidgetWelcomeVariant,
  AgentWidgetWelcomeDismiss
} from "./types";

// Custom conversation-history backends (`features.history.provider`). The
// provider registry beside this module stays private.
export type {
  HistoryProvider,
  HistoryOperationContext,
  PreparedHistoryActivation,
  HistoryListOptions,
  HistoryListResult,
  HistoryPageOptions,
  HistoryPageResult,
  HistoryDeleteAllOptions,
  HistoryProviderErrorCode,
} from "./internal/history-provider";

// Value export: custom providers construct and `instanceof` this.
export {
  HistoryProviderError,
  isHistoryProviderError,
} from "./internal/history-provider";

export {
  applyFeaturePreferences,
  parseWidgetPreferenceSlice,
  resolveConfigPreferences,
  normalizeMediaType,
} from "./utils/feature-preferences";

export type {
  WidgetPreferenceParseIssueCode,
  WidgetPreferenceParseIssue,
  WidgetPreferenceParseResult,
} from "./utils/feature-preferences";

export {
  canonicalArtifactDisplayMode,
  resolveArtifactDisplay,
  resolveArtifactDisplayMode,
} from "./utils/artifact-display";

export type {
  PersonaArtifactDisplayDescriptor,
  PersonaArtifactDisplayMatch,
  PersonaArtifactDisplayResolution,
} from "./utils/artifact-display";

export type {
  RuntypeExecutionStreamEvent,
  RuntypeStreamEventOf,
  RuntypeTurnCompleteEvent,
  RuntypeStepCompleteEvent,
  RuntypeStopReasonKind,
  RuntypeClientInitRequest,
  RuntypeClientInitResponse,
  RuntypeClientChatRequest,
  RuntypeClientChatStreamEvent,
  RuntypeClientResumeRequest,
  RuntypeClientResumeStreamEvent,
  RuntypeClientFeedbackRequest,
  RuntypeClientFeedbackResponse,
  RuntypeClientFeedbackType,
} from "./generated/runtype-openapi-contract";

import type { RuntypeExecutionStreamEvent } from "./generated/runtype-openapi-contract";

/** @deprecated FlowSSEEvent was folded into the unified ExecutionStreamEvent
 *  (Runtype PR #5722). Use RuntypeExecutionStreamEvent instead. */
export type RuntypeFlowSSEEvent = RuntypeExecutionStreamEvent;

export {
  ASK_USER_QUESTION_TOOL_NAME,
  createAskUserQuestionBubble,
  ensureAskUserQuestionSheet,
  removeAskUserQuestionSheet,
  isAskUserQuestionMessage,
  parseAskUserQuestionPayload
} from "./components/ask-user-question-bubble";

export {
  ASK_USER_QUESTION_CLIENT_TOOL,
  ASK_USER_QUESTION_PARAMETERS_SCHEMA,
  builtInClientToolsForDispatch
} from "./ask-user-question-tool";

export {
  SUGGEST_REPLIES_TOOL_NAME,
  SUGGEST_REPLIES_CLIENT_TOOL,
  SUGGEST_REPLIES_PARAMETERS_SCHEMA,
  isSuggestRepliesMessage,
  parseSuggestRepliesPayload,
  latestAgentSuggestions
} from "./suggest-replies-tool";

export { initAgentWidgetFn as initAgentWidget };
export { getAgentWidgetHandlesFn as getAgentWidgetHandles };
export {
  createWidgetHostLayout,
  type WidgetHostLayout,
  type WidgetHostLayoutMode
} from "./runtime/host-layout";
export {
  createAgentExperience,
  type AgentWidgetController
} from "./ui";
export {
  AgentWidgetSession,
  type AgentWidgetSessionStatus
} from "./session";
export { AgentWidgetClient } from "./client";
export type { SSEEventCallback } from "./client";
// `WebMcpBridge` (the class VALUE) ships from `index.ts` (npm only): the CDN
// global constructs the runtime lazily inside client.ts from the
// webmcp-runtime chunk, so the class stays out of the IIFE payload.
export {
  WEBMCP_TOOL_PREFIX,
  isWebMcpToolName,
  stripWebMcpPrefix
} from "./webmcp-bridge";
export { createLocalStorageAdapter } from "./utils/storage";
export {
  createActionManager,
  defaultActionHandlers,
  defaultJsonActionParser
} from "./utils/actions";
export {
  markdownPostprocessor,
  preprocessStreamingMarkdown,
  escapeHtml,
  directivePostprocessor,
  createMarkdownProcessor,
  createMarkdownProcessorFromConfig,
  createDirectivePostprocessor
} from "./postprocessors";
export type { MarkdownProcessorOptions } from "./postprocessors";
// Markdown parser-readiness escape hatch. On the IIFE/CDN build `marked` +
// `DOMPurify` load lazily, so content injected right after init can render as
// escaped plain text until the chunk resolves. Hosts can `await
// loadMarkdownParsers()` before injecting, or `onMarkdownParsersReady(cb)` to be
// told when they land (both resolve/no-op instantly on the npm build, where the
// parsers are bundled eagerly).
export {
  loadMarkdownParsers,
  onMarkdownParsersReady
} from "./markdown-parsers-loader";
export type { MarkdownParsersModule } from "./markdown-parsers-loader";
export {
  createDefaultSanitizer,
  resolveSanitizer
} from "./utils/sanitize";
export type { SanitizeFunction } from "./utils/sanitize";
export {
  createPlainTextParser,
  createJsonStreamParser,
  createFlexibleJsonStreamParser,
  createRegexJsonParser,
  createXmlParser
} from "./utils/formatting";
export {
  // Multi-modal content utilities
  normalizeContent,
  getDisplayText,
  hasImages,
  getImageParts,
  createTextPart,
  createImagePart,
  fileToImagePart,
  validateImageFile
} from "./utils/content";
// Context mention helper TYPES only. The value helpers (defaultMentionFilter,
// createStaticMentionSource, createSlashCommandsSource) ship from `index.ts`
// (npm) so the CDN global doesn't carry mention-matcher: script-tag hosts pass
// plain source objects in config; no shipped demo reaches these via the global.
export type { SlashCommandDefinition } from "./utils/mention-matcher";
export {
  collectEnrichedPageContext,
  formatEnrichedContext,
  generateStableSelector,
  defaultParseRules
} from "./utils/dom-context";
export type {
  EnrichedPageElement,
  DomContextOptions,
  DomContextMode,
  ParseOptionsConfig,
  ParseRule,
  RuleScoringContext,
  FormatEnrichedContextOptions
} from "./utils/dom-context";
export {
  AttachmentManager,
  type PendingAttachment,
  type AttachmentManagerConfig
} from "./utils/attachment-manager";
export {
  generateMessageId,
  generateUserMessageId,
  generateAssistantMessageId
} from "./utils/message-id";
export { isDockedMountMode, resolveDockConfig } from "./utils/dock";
// NOTE: `generateCodeSnippet` (dev/config-tool helper) is intentionally NOT
// re-exported here so it stays out of the IIFE/CDN bundle (index-global.ts
// re-exports from this module). It is re-added to the npm barrel in `index.ts`.
export { VERSION } from "./version";
export type { AgentWidgetInitHandle };

// Plugin system exports
export type {
  AgentWidgetPlugin,
  AgentWidgetTransformSuggestionsContext,
  AgentWidgetRenderSuggestionContext,
  AgentWidgetRenderWelcomeContext,
  AgentWidgetSuggestionSelectContext,
  AgentWidgetRailSection,
  AgentWidgetRailSectionContext
} from "./plugins/types";
// Welcome resolver: `renderWelcome` receives the resolved shape.
export { resolveWelcomeConfig, isWelcomeVisible } from "./welcome";
export type { ResolvedWelcomeConfig } from "./welcome";
export type { AgentWidgetPluginStorage } from "./utils/plugin-storage";
export { pluginRegistry } from "./plugins/registry";

// Stream animation plugin API: lets consumers register custom animations
// that match the core built-ins (typewriter, pop-bubble, letter-rise,
// word-fade) and the subpath modules (wipe, glyph-cycle).
export {
  registerStreamAnimationPlugin,
  unregisterStreamAnimationPlugin,
  listRegisteredStreamAnimations,
} from "./utils/stream-animation";
export type {
  StreamAnimationPlugin,
  StreamAnimationContext,
  AgentWidgetStreamAnimationBuffer,
  AgentWidgetStreamAnimationBuiltinType,
  AgentWidgetStreamAnimationType,
  AgentWidgetStreamAnimationFeature,
  AgentWidgetStreamAnimationPlaceholder,
  AgentWidgetScrollMode,
  AgentWidgetScrollRestorePosition,
  AgentWidgetScrollBehaviorFeature,
  AgentWidgetScrollToBottomFeature,
  AgentWidgetScrollToBottomIconState,
  AgentWidgetScrollToBottomIconContext,
  AgentWidgetScrollToBottomIconHandle,
  AgentWidgetComponentRenderer,
  AgentWidgetComponentRenderContext,
  AgentWidgetComponentRenderHandle,
} from "./types";

// Action system types: needed to type the `actionHandlers` / `actionParsers`
// config options and to author custom handlers/parsers.
export type {
  AgentWidgetActionHandler,
  AgentWidgetActionHandlerResult,
  AgentWidgetActionParser,
  AgentWidgetParsedAction,
  AgentWidgetActionContext,
  AgentWidgetActionEventPayload,
} from "./types";

// Dropdown utility exports
export { createDropdownMenu } from "./utils/dropdown";
export type { DropdownMenuItem, CreateDropdownOptions, DropdownMenuHandle } from "./utils/dropdown";

// Icon utility exports
export { renderLucideIcon, registerIcons } from "./utils/icons";
export type { IconName } from "./utils/icons";
export type { IconNode } from "lucide";

// Button utility exports
export { createIconButton, createLabelButton, createToggleGroup, createComboButton, createSplitButton } from "./utils/buttons";
export type {
  CreateIconButtonOptions,
  CreateLabelButtonOptions,
  CreateToggleGroupOptions,
  ToggleGroupItem,
  ToggleGroupHandle,
  CreateComboButtonOptions,
  ComboButtonHandle
} from "./utils/buttons";

// NOTE: `createDemoCarousel` (demo-only component) is intentionally NOT
// re-exported here so it stays out of the IIFE/CDN bundle. It is re-added to
// the npm barrel in `index.ts`.

// Theme system exports
export {
  createTheme,
  resolveTokens,
  themeToCssVariables,
  applyThemeVariables,
  getActiveTheme,
  getColorScheme,
  detectColorScheme,
  createThemeObserver
} from "./utils/theme";
export {
  DEFAULT_PALETTE,
  DEFAULT_SEMANTIC,
  DEFAULT_COMPONENTS,
  validateTheme,
  THEME_ZONES
} from "./utils/tokens";
export type { ThemeZone } from "./utils/tokens";
// Theme-plugin factories live in index.ts (npm-only): no runtime path builds
// them, so exporting here would ship all of utils/plugins.ts in the CDN IIFE.
export type {
  DeepPartial,
  BrandConfig,
  PersonaTheme,
  PersonaThemePlugin,
  CreateThemeOptions,
  TokenReference,
  ColorShade,
  ColorPalette,
  SpacingScale,
  TypographyScale,
  ShadowScale,
  BorderScale,
  RadiusScale,
  SemanticColors,
  SemanticSpacing,
  SemanticTypography,
  ComponentTokens,
  MessageTokens,
  MessageGeometryTokens,
  ComposerOverflowMenuTokens,
  TextStyleTokens,
  AttachmentTokens,
  AttachmentRemoveTokens,
  ArtifactToolbarTokens,
  ArtifactTabTokens,
  ArtifactPaneTokens,
  IconButtonTokens,
  LabelButtonTokens,
  ToggleGroupTokens,
  ThemeValidationResult,
  ThemeValidationError
} from "./types/theme";

// Component system exports
export { componentRegistry } from "./components/registry";
export type {
  ComponentRenderer,
  ComponentContext,
  ComponentRegistrationOptions
} from "./components/registry";
export {
  createComponentStreamParser,
  isComponentDirectiveType
} from "./utils/component-parser";
export type { ComponentDirective } from "./utils/component-parser";
export {
  renderComponentDirective,
  createComponentMiddleware,
  hasComponentDirective,
  extractComponentDirectiveFromMessage
} from "./utils/component-middleware";

// Default configuration exports
export {
  DEFAULT_WIDGET_CONFIG,
  DEFAULT_FLOATING_LAUNCHER_MAX_WIDTH,
  DEFAULT_FLOATING_LAUNCHER_WIDTH,
  mergeWithDefaults
} from "./defaults";
export {
  PRESETS,
  getPreset,
  PRESET_SHOP,
  PRESET_MINIMAL,
  PRESET_FULLSCREEN
} from "./presets";
export type { WidgetPreset } from "./presets";

// Layout system exports
export {
  buildHeader,
  buildComposer,
  attachHeaderToContainer
} from "./components/panel";
export type {
  HeaderElements,
  HeaderBuildContext,
  ComposerElements,
  ComposerBuildContext
} from "./components/panel";
export {
  headerLayouts,
  getHeaderLayout,
  buildHeaderWithLayout,
  buildDefaultHeader,
  buildMinimalHeader
} from "./components/header-layouts";
export type {
  HeaderLayoutContext,
  HeaderLayoutRenderer
} from "./components/header-layouts";
export {
  createStandardBubble,
  createBubbleWithLayout,
  createTypingIndicator,
  createMessageActions,
  renderLoadingIndicatorWithFallback
} from "./components/message-bubble";
export type {
  MessageTransform,
  MessageActionCallbacks,
  LoadingIndicatorRenderer,
  CreateStandardBubbleOptions
} from "./components/message-bubble";
export {
  createCSATFeedback,
  createNPSFeedback
} from "./components/feedback";
export type { CSATFeedbackOptions, NPSFeedbackOptions } from "./components/feedback";

// Voice module TYPE exports. The factory VALUES (createVoiceProvider,
// createBestAvailableVoiceProvider, isVoiceSupported) ship from `index.ts`
// (npm) so the CDN global doesn't carry the provider runtime — the CDN path
// reaches it lazily via the voice-runtime chunk inside session.setupVoice.
export type {
  VoiceProvider,
  VoiceResult,
  VoiceStatus,
  VoiceConfig,
  VoiceMetrics,
  VoicePlaybackEngine,
  VoiceTranscriptMetadata,
  VoiceDelegationRequest,
  VoiceDelegationResult,
  VoiceDelegationStatus,
  VoiceDelegationFollowUp,
  VoiceSessionBridge,
  VoiceClientTools,
  VoiceClientToolsSnapshot,
  VoiceClientToolsChannel,
  VoiceClientToolsSession,
  VoiceClientToolOutcome,
  VoiceClientToolStatus,
  VoiceDirectTurn,
  ClientToolsFrame,
  ClientToolCallFrame,
  ClientToolResultFrame,
  PcmStreamPlayer,
  AgentWidgetVoiceStatusEvent,
  ReadAloudState,
  SpeechEngine,
  SpeechRequest,
  SpeechCallbacks
} from "./types";

// Direct-file imports (NOT the ./voice barrel): the barrel re-exports the
// provider factory + providers, which live in the lazy voice-runtime chunk
// and must not be dragged back into the core bundles.
export {
  BrowserSpeechEngine,
  pickBestVoice
} from "./voice/browser-speech-engine";
export { ReadAloudController } from "./voice/read-aloud-controller";
export type {
  BrowserSpeechEngineOptions,
  ReadAloudListener,
  FallbackSpeechEngineOptions
} from "./voice";
// RuntypeSpeechEngine + FallbackSpeechEngine ship from the
// `@runtypelabs/persona/voice-worklet-player` subpath, not here: keeping the
// hosted read-aloud engine out of the main bundle lets the IIFE/CDN build defer
// it to the `runtype-tts.js` chunk (loaded only for `provider: 'runtype'`). The
// blessed way to use it is `textToSpeech: { provider: 'runtype' }` — no import
// needed.

export default initAgentWidgetFn;
